import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import {
  ClipboardList,
  Brain,
  Target,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Clock,
  Sparkles,
  FileText,
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Download,
  Share2,
  TrendingUp,
  BarChart3,
  History,
  Play,
  Euro,
  CreditCard,
  Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssessmentCenter = () => {
  const { language } = useLanguage();
  const { token, getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tests'); // tests, results, history
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState([]);
  const [expandedTestId, setExpandedTestId] = useState(null);
  const [assignedTestsData, setAssignedTestsData] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(() => {
    // Check if user already accepted privacy for tests in this session
    return localStorage.getItem('imt_test_privacy_accepted') === 'true';
  });

  const toggleTestDescription = (testId, e) => {
    e.stopPropagation();
    setExpandedTestId(expandedTestId === testId ? null : testId);
  };

  // Fetch assigned tests for the client
  useEffect(() => {
    const fetchAssignedTests = async () => {
      if (token && user?.role !== 'specialist') {
        try {
          const response = await axios.get(`${API}/client/my-tests`, {
            headers: getAuthHeader()
          });
          setAssignedTestsData(response.data);
        } catch (error) {
          console.error('Error fetching assigned tests:', error);
        }
      }
    };
    fetchAssignedTests();
  }, [token, user, getAuthHeader]);

  // Fetch available tests
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const response = await axios.get(`${API}/tests`);
        setTests(response.data);
        setFilteredTests(response.data);
      } catch (error) {
        console.error('Error fetching tests:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
  }, []);

  // Fetch user's test results
  useEffect(() => {
    const fetchResults = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/results`, {
            headers: getAuthHeader()
          });
          setTestResults(response.data || []);
        } catch (error) {
          console.error('Error fetching results:', error);
        }
      }
    };
    fetchResults();
  }, [token, getAuthHeader]);

  // Filter tests
  useEffect(() => {
    let filtered = tests;
    
    if (searchQuery) {
      filtered = filtered.filter(test => 
        test.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        test.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(test => test.category === selectedCategory);
    }
    
    setFilteredTests(filtered);
  }, [searchQuery, selectedCategory, tests]);

  const categories = [
    { id: 'all', label: language === 'it' ? 'Tutti' : 'All' },
    { id: 'depression', label: language === 'it' ? 'Depressione' : 'Depression' },
    { id: 'anxiety', label: language === 'it' ? 'Ansia' : 'Anxiety' },
    { id: 'personality', label: language === 'it' ? 'Personalità' : 'Personality' },
    { id: 'emotional', label: language === 'it' ? 'Emotivo' : 'Emotional' },
    { id: 'cognitive', label: language === 'it' ? 'Cognitivo' : 'Cognitive' }
  ];

  const getTestIcon = (category) => {
    switch (category) {
      case 'depression': return '😔';
      case 'anxiety': return '😰';
      case 'personality': return '🎭';
      case 'emotional': return '💭';
      case 'cognitive': return '🧠';
      default: return '📋';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'depression': return 'bg-blue-100 text-blue-700';
      case 'anxiety': return 'bg-amber-100 text-amber-700';
      case 'personality': return 'bg-purple-100 text-purple-700';
      case 'emotional': return 'bg-rose-100 text-rose-700';
      case 'cognitive': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const startTest = (testId) => {
    if (!privacyAccepted) {
      // Scroll to the consent checkbox
      document.getElementById('assessment-privacy-consent')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Show payment modal instead of navigating directly
    setSelectedTestId(testId);
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = () => {
    // For now, show message that payment is coming soon
    // In production, this would integrate with Satispay
    setShowPaymentModal(false);
    // For demo, show a toast about payment integration
    alert(language === 'it' 
      ? 'Integrazione Satispay in arrivo! Contattaci per informazioni sul pagamento.' 
      : 'Satispay integration coming soon! Contact us for payment information.');
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-[#2563eb]" />
            {language === 'it' ? 'Centro Valutazione' : 'Assessment Center'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'it' 
              ? 'Test psicologici standardizzati - risultati inviati allo specialista' 
              : 'Standardized psychological tests - results sent to specialist'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-100 text-emerald-700 text-sm px-3 py-1">
            <CheckCircle className="w-4 h-4 mr-1" />
            {language === 'it' ? 'Test Psicologici' : 'Psychological Tests'}
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-100 rounded-2xl p-1">
        {[
          { id: 'tests', label: language === 'it' ? 'Test Disponibili' : 'Available Tests', icon: ClipboardList },
          { id: 'results', label: language === 'it' ? 'I Miei Report' : 'My Reports', icon: FileText },
          { id: 'history', label: language === 'it' ? 'Storico' : 'History', icon: History }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white shadow text-primary'
                : 'text-muted-foreground hover:text-primary'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tests Tab */}
      {activeTab === 'tests' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Search and Filter */}
          <Card className="border-0 shadow-md rounded-2xl">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder={language === 'it' ? 'Cerca test...' : 'Search tests...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-xl"
                  />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                        selectedCategory === cat.id
                          ? 'bg-[#2563eb] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Banner */}
          <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-r from-[#2563eb] to-blue-500">
            <CardContent className="p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">
                    {language === 'it' ? 'Valutazione Professionale' : 'Professional Assessment'}
                  </h3>
                  <p className="text-white/90 text-sm">
                    {language === 'it'
                      ? 'Completa un test e i risultati verranno inviati direttamente allo specialista che ti contatterà per la restituzione del report completo.'
                      : 'Complete a test and the results will be sent directly to the specialist who will contact you to deliver the complete report.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Required Banner */}
          <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-r from-emerald-500 to-blue-500 text-white overflow-hidden">
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Euro className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">
                      {language === 'it' ? 'Test Psicologico Professionale' : 'Professional Psychological Test'}
                    </h3>
                    <p className="text-emerald-100 text-sm">
                      {language === 'it' 
                        ? 'Il costo include la valutazione e il report personalizzato' 
                        : 'Cost includes assessment and personalized report'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center md:text-right">
                    <div className="flex items-baseline gap-2 justify-center md:justify-end">
                      <span className="text-lg text-emerald-200 line-through">€299</span>
                      <span className="text-3xl font-bold">€199</span>
                    </div>
                    <div className="text-xs text-emerald-100">
                      {language === 'it' ? 'Promo per test' : 'Promo per test'}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-xs text-emerald-100 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {language === 'it' 
                    ? 'Il pagamento verrà richiesto prima di iniziare il test tramite Satispay (integrazione in arrivo)' 
                    : 'Payment will be required before starting the test via Satispay (integration coming soon)'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Consent Checkbox */}
          {!privacyAccepted && (
            <Card className="border-2 border-blue-300 shadow-md rounded-2xl bg-blue-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <FileText className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                  <div className="flex-grow">
                    <h3 className="font-bold text-lg text-blue-900 mb-2">
                      {language === 'it' ? 'Consenso Obbligatorio' : 'Required Consent'}
                    </h3>
                    <p className="text-sm text-blue-700 mb-4">
                      {language === 'it'
                        ? 'Prima di accedere ai test, è necessario accettare l\'informativa sulla privacy e il consenso informato.'
                        : 'Before accessing the tests, you must accept the privacy policy and informed consent.'}
                    </p>
                    <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-blue-200">
                      <input
                        type="checkbox"
                        id="assessment-privacy-consent"
                        checked={privacyAccepted}
                        onChange={(e) => {
                          setPrivacyAccepted(e.target.checked);
                          if (e.target.checked) {
                            localStorage.setItem('imt_test_privacy_accepted', 'true');
                          }
                        }}
                        className="mt-1 w-5 h-5 rounded border-gray-300 text-[#2563eb] focus:ring-[#2563eb] cursor-pointer"
                        data-testid="assessment-privacy-consent-checkbox"
                      />
                      <label htmlFor="assessment-privacy-consent" className="text-sm text-gray-700 cursor-pointer">
                        {language === 'it' ? (
                          <>
                            Ho letto e accetto l'<Link to="/privacy-policy" className="text-[#2563eb] hover:underline font-medium" target="_blank">Informativa sulla Privacy</Link> e il <Link to="/terms-conditions" className="text-[#2563eb] hover:underline font-medium" target="_blank">Consenso Informato</Link>. *
                          </>
                        ) : (
                          <>
                            I have read and accept the <Link to="/privacy-policy" className="text-[#2563eb] hover:underline font-medium" target="_blank">Privacy Policy</Link> and the <Link to="/terms-conditions" className="text-[#2563eb] hover:underline font-medium" target="_blank">Informed Consent</Link>. *
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assigned Tests Status - Show for clients */}
          {assignedTestsData && (
            <Card className={`border-0 shadow-lg rounded-2xl ${
              assignedTestsData.all_completed 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                : assignedTestsData.assigned_tests?.length > 0
                  ? 'bg-gradient-to-r from-blue-500 to-blue-500 text-white'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
            }`}>
              <CardContent className="p-6">
                {assignedTestsData.all_completed ? (
                  <div className="text-center py-4">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">
                      {language === 'it' ? 'Test Completati!' : 'Tests Completed!'}
                    </h3>
                    <p className="text-white/90">
                      {language === 'it' 
                        ? 'Hai completato tutti i test assegnati. Lo specialista ti contatterà per la restituzione dei risultati.'
                        : 'You have completed all assigned tests. The specialist will contact you for the results.'}
                    </p>
                  </div>
                ) : assignedTestsData.assigned_tests?.length > 0 ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">
                        {language === 'it' ? 'I Tuoi Test Assegnati' : 'Your Assigned Tests'}
                      </h3>
                      <Badge className="bg-white/20 text-white">
                        {assignedTestsData.total_completed}/{assignedTestsData.total_assigned} completati
                      </Badge>
                    </div>
                    <Progress 
                      value={(assignedTestsData.total_completed / assignedTestsData.total_assigned) * 100} 
                      className="h-2 bg-white/20"
                    />
                    <p className="text-sm text-white/80 mt-2">
                      {language === 'it' 
                        ? 'Completa tutti i test per ricevere la valutazione dello specialista'
                        : 'Complete all tests to receive the specialist evaluation'}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold mb-2">
                      {language === 'it' ? 'Nessun Test Assegnato' : 'No Tests Assigned'}
                    </h3>
                    <p className="text-white/90 text-sm">
                      {language === 'it' 
                        ? 'Lo specialista ti assegnerà i test dopo il colloquio conoscitivo.'
                        : 'The specialist will assign tests after the initial consultation.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tests Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#2563eb] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-muted-foreground mt-4">
                {language === 'it' ? 'Caricamento test...' : 'Loading tests...'}
              </p>
            </div>
          ) : assignedTestsData && assignedTestsData.assigned_tests?.length === 0 && !assignedTestsData.all_completed ? (
            // Show nothing if no tests assigned (message already shown above)
            null
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(assignedTestsData?.assigned_tests?.length > 0 
                ? assignedTestsData.assigned_tests 
                : filteredTests
              ).map((test, index) => {
                // Handle both full test objects and assigned test objects
                const testData = test.id ? test : tests.find(t => t.id === test) || test;
                const isCompleted = assignedTestsData?.completed_tests?.includes(testData.id || test);
                
                // Handle multilingual fields
                const testName = typeof testData.name === 'object' 
                  ? (testData.name[language] || testData.name.it || testData.name.en) 
                  : testData.name;
                const testDescription = typeof testData.description === 'object'
                  ? (testData.description[language] || testData.description.it || testData.description.en)
                  : testData.description;
                const testCategory = typeof testData.category === 'object'
                  ? (testData.category[language] || testData.category.it || testData.category.en)
                  : testData.category;
                
                return (
                <motion.div
                  key={testData.id || test}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`border-0 shadow-md rounded-2xl hover:shadow-lg transition-all group h-full ${isCompleted ? 'opacity-70' : ''}`}>
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex items-start justify-between mb-3">
                        <div className="text-3xl">{getTestIcon(testCategory)}</div>
                        <div className="flex gap-2">
                          {isCompleted && (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completato
                            </Badge>
                          )}
                          <Badge className={getCategoryColor(testCategory)}>
                            {testCategory || 'General'}
                          </Badge>
                        </div>
                      </div>
                      
                      <h3 className="font-bold text-primary text-lg mb-2 group-hover:text-[#2563eb] transition-colors">
                        {testName}
                      </h3>
                      
                      {/* Full Description */}
                      <div className="mb-4 flex-grow">
                        <p className="text-sm text-muted-foreground">
                          {testDescription || (language === 'it' ? 'Test di valutazione psicologica standardizzato per una diagnosi accurata.' : 'Standardized psychological assessment test for accurate diagnosis.')}
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <ClipboardList className="w-3.5 h-3.5" />
                            {testData.questions?.length || '10+'} {language === 'it' ? 'domande' : 'questions'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            ~{Math.ceil((testData.questions?.length || 10) / 2)} min
                          </span>
                        </div>
                        <Button 
                          size="sm" 
                          className={`rounded-full h-8 px-3 ${isCompleted ? 'bg-gray-400' : 'bg-[#2563eb] hover:bg-[#1d4ed8]'}`}
                          onClick={() => startTest(testData.id)}
                          disabled={isCompleted}
                        >
                          {isCompleted ? (
                            <>
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              {language === 'it' ? 'Fatto' : 'Done'}
                            </>
                          ) : (
                            <>
                              <Play className="w-3.5 h-3.5 mr-1" />
                              {language === 'it' ? 'Inizia' : 'Start'}
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
              })}
            </div>
          )}

          {filteredTests.length === 0 && !loading && (
            <Card className="border-0 shadow-md rounded-2xl">
              <CardContent className="p-12 text-center">
                <Search className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-semibold text-primary mb-2">
                  {language === 'it' ? 'Nessun test trovato' : 'No tests found'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'it' 
                    ? 'Prova a modificare i filtri di ricerca' 
                    : 'Try adjusting your search filters'}
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {testResults.length === 0 ? (
            <Card className="border-0 shadow-lg rounded-3xl">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">
                  {language === 'it' ? 'Nessun Report Ancora' : 'No Reports Yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {language === 'it'
                    ? 'Completa il tuo primo test per ricevere un report dettagliato'
                    : 'Complete your first test to receive a detailed report'}
                </p>
                <Button
                  className="rounded-full bg-[#2563eb] hover:bg-[#1d4ed8]"
                  onClick={() => setActiveTab('tests')}
                >
                  <Play className="w-4 h-4 mr-2" />
                  {language === 'it' ? 'Inizia un Test' : 'Start a Test'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-r from-emerald-500 to-blue-500">
                <CardContent className="p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-lg">
                        {language === 'it' ? 'I Tuoi Report' : 'Your Reports'}
                      </h3>
                      <p className="text-white/80 text-sm">
                        {testResults.length} {language === 'it' ? 'test completati' : 'tests completed'}
                      </p>
                    </div>
                    <BarChart3 className="w-10 h-10 text-white/80" />
                  </div>
                </CardContent>
              </Card>

              {testResults.map((result, index) => (
                <Card key={index} className="border-0 shadow-md rounded-2xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-[#2563eb]/10 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-[#2563eb]" />
                        </div>
                        <div>
                          <h4 className="font-bold text-primary">{result.test_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {new Date(result.completed_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {result.sent_to_specialist ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {language === 'it' ? 'Inviato allo Specialista' : 'Sent to Specialist'}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            {language === 'it' ? 'In elaborazione' : 'Processing'}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                      <p className="text-sm text-blue-700">
                        {language === 'it' 
                          ? 'I risultati sono stati inviati allo specialista. Verrai contattato per la restituzione del report.'
                          : 'Results have been sent to the specialist. You will be contacted for the report delivery.'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </motion.div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-0 shadow-lg rounded-3xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#2563eb]" />
                {language === 'it' ? 'Progressi nel Tempo' : 'Progress Over Time'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {testResults.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-muted-foreground">
                    {language === 'it' 
                      ? 'Completa più test per vedere i tuoi progressi nel tempo'
                      : 'Complete more tests to see your progress over time'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {language === 'it'
                      ? 'Grafico dei progressi disponibile dopo 3+ test completati'
                      : 'Progress chart available after 3+ completed tests'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
          >
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold">
                {language === 'it' ? 'Pagamento Richiesto' : 'Payment Required'}
              </h2>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-blue-600 mb-2">€199</p>
                <p className="text-sm text-muted-foreground line-through mb-1">€249</p>
                <p className="text-muted-foreground">
                  {language === 'it' 
                    ? 'Pacchetto Test Psicologici Completo' 
                    : 'Complete Psychological Test Package'}
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-blue-800 mb-2">
                  {language === 'it' ? 'Include:' : 'Includes:'}
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• {language === 'it' ? 'Tutti i test assegnati' : 'All assigned tests'}</li>
                  <li>• {language === 'it' ? 'Report PDF dettagliati' : 'Detailed PDF reports'}</li>
                  <li>• {language === 'it' ? 'Invio automatico allo specialista' : 'Automatic specialist delivery'}</li>
                  <li>• {language === 'it' ? 'Colloquio con lo specialista' : 'Specialist consultation'}</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={async () => {
                    try {
                      const response = await axios.post(`${API}/api/payments/checkout`, {
                        package_id: 'complete_assessment',
                        origin_url: window.location.origin,
                        user_id: user?.id,
                        user_email: user?.email
                      });
                      if (response.data.checkout_url) {
                        window.location.href = response.data.checkout_url;
                      }
                    } catch (error) {
                      console.error('Payment error:', error);
                      toast.error(language === 'it' ? 'Errore nel pagamento' : 'Payment error');
                    }
                  }}
                  className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {language === 'it' ? 'Paga con Carta' : 'Pay with Card'}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  className="w-full h-12 rounded-xl"
                >
                  {language === 'it' ? 'Annulla' : 'Cancel'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AssessmentCenter;
